import { Injectable, Optional, Inject } from '@angular/core';
import { orderBy } from 'lodash';
import { LocalStorageService } from '@shared/utils/local-storage.service';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { TrustStructureServiceProxy, TrustStructureDto } from '@shared/service-proxies/service-proxies';
import { BehaviorSubject } from 'rxjs';
import * as moment from 'moment';

export const USE_STORAGE = 'USE_STORAGE';
export const FILTERS_STORAGE_KEY = 'FILTERS_STORAGE_KEY';

export interface IFilters {
    sitesIds: Array<number>;
    specialitiesIds: Array<number>;
    consultantsIds: Array<number>;
}

export interface IDateRange {
    startDate: moment.Moment;
    endDate: moment.Moment;
}

const DefaultFilters: IFilters = {
    sitesIds: [],
    specialitiesIds: [],
    consultantsIds: []
};

@Injectable()
export class ListFilterService {
    private _storageUserKey: string;
    private _sitesIds: Array<number> = [];
    private _specialitiesIds: Array<number> = [];
    private _consultantsIds: Array<number> = [];
    private _trustStructure: any = new TrustStructureDto();
    private _fitersSource = new BehaviorSubject(DefaultFilters);

    private _initialStartDate: moment.Moment = moment().startOf('day');
    private _initialEndDate: moment.Moment = this._initialStartDate.clone().add(6, 'week').endOf('day');
    private _selectedDateRange: IDateRange = {
        startDate: this._initialStartDate,
        endDate: this._initialEndDate
    };

    filter = this._fitersSource.asObservable();

    get sitesIds(): Array<number> {
        return this._sitesIds;
    }

    get specialitiesIds(): Array<number> {
        return this._specialitiesIds;
    }

    get consultantsIds(): Array<number> {
        return this._consultantsIds;
    }

    get trustStructure(): any {
        return this._trustStructure;
    }

    get siteNames(): Array<string> {
        return this._trustStructure.sites.filter((s) => this._sitesIds.some((id) => id === s.id)).map((s) => s.name);
    }

    get specialityNames(): Array<string> {
        return this._trustStructure.specialities.filter((s) => this._specialitiesIds.some((id) => id === s.id)).map((s) => s.name);
    }

    get consultantNames(): Array<string> {
        return this._trustStructure.consultants.filter((c) => this._consultantsIds.some((id) => id === c.id)).map((s) => s.name);
    }

    get selectedDateRange(): IDateRange {
        return this._selectedDateRange;
    }

    set selectedDateRange(dateRange: IDateRange) {
        this.selectedDateRange.startDate = dateRange.startDate;
        this.selectedDateRange.endDate = dateRange.endDate;
    }

    constructor(
        private _trustStructureServiceProxy: TrustStructureServiceProxy,
        private _sessionService: AppSessionService,
        private _localStorageService: LocalStorageService,
        @Inject(USE_STORAGE)
        @Optional()
        private _useStorage?: boolean
    ) {
        this._useStorage = this._useStorage === null ? true : this._useStorage;

        this._trustStructureServiceProxy.getTrustTreeStructure().subscribe((response: any) => {
            this._trustStructure.sites = orderBy(response.sites, (s) => s.name);
            this._trustStructure.specialities = orderBy(response.specialities, (s) => s.name);
            this._trustStructure.consultants = orderBy(response.consultants, (c) => c.name);
            if (this._useStorage) {
                this._storageUserKey = `${FILTERS_STORAGE_KEY}/${this._sessionService.userId}`;
                this.initializeFilters();
            } else {
                this.reset();
            }
        });
    }

    selectSites(siteIds: Array<number>) {
        if (!!siteIds) {
            this._sitesIds = siteIds;
            if (this._trustStructure.specialities) {
                let specIdsInSites: Array<number> = this._trustStructure.specialities
                    .filter((spec) => spec.parentIds.some((pId) => siteIds.some((sId) => sId === pId)))
                    .map((s) => s.id);
                if (specIdsInSites.length === 1) {
                    this.selectSpecialities(specIdsInSites);
                } else {
                    this.selectSpecialities(this._specialitiesIds.filter((sId) => specIdsInSites.some((id) => id === sId)));
                }
            }
        }
    }

    selectSpecialities(specialityIds: Array<number>) {
        if (!!specialityIds) {
            this._specialitiesIds = specialityIds;
            if (this._trustStructure.consultants) {
                let consultantsIdsInSpecialities: Array<number> = this._trustStructure.consultants
                    .filter((cons) => cons.parentIds.some((cId) => specialityIds.some((sId) => sId === cId)))
                    .map((c) => c.id);
                if (consultantsIdsInSpecialities.length === 1) {
                    this.selectConsultants(consultantsIdsInSpecialities);
                } else {
                    this.selectConsultants(this._consultantsIds.filter((cId) => consultantsIdsInSpecialities.some((id) => id === cId)));
                }
            }
        }
    }

    selectConsultants(consultantIds: Array<number>) {
        if (!!consultantIds) {
            this._consultantsIds = consultantIds;
        }
        const newFilters: IFilters = {
            sitesIds: this.sitesIds,
            specialitiesIds: this.specialitiesIds,
            consultantsIds: this.consultantsIds
        };
        this.saveFilters(newFilters);
        this._fitersSource.next(newFilters);
    }

    reset() {
        this.trustStructure.sites && this._trustStructure.sites.length === 1
            ? this.selectSites(this._trustStructure.sites.map((s) => s.id))
            : this.selectSites([]);
    }

    private async initializeFilters(): Promise<any> {
        return new Promise<void>((resolve) => {
            this._localStorageService.getItem(this._storageUserKey, (err, item) => {
                if (!err && item) {
                    const filters: IFilters = JSON.parse(item);
                    this._sitesIds = filters.sitesIds;
                    this._specialitiesIds = filters.specialitiesIds;
                    this._consultantsIds = filters.consultantsIds;
                    this._fitersSource.next(filters);
                } else {
                    const filters: IFilters = {
                        sitesIds: this._sitesIds,
                        specialitiesIds: this._specialitiesIds,
                        consultantsIds: this._consultantsIds
                    };
                    this.saveFilters(filters);
                }
                resolve();
            });
        });
    }

    private saveFilters(filters: IFilters) {
        if (this._useStorage) {
            this._localStorageService.setItem(this._storageUserKey, JSON.stringify(filters));
        }
    }
}




// WEBPACK FOOTER //
// ./src/app/shared/services/list-filter.service.ts